import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if it's a malformed JWT error
      const data = error.response?.data;
      if (data?.code === 'JWT_MALFORMED' || data?.action === 'CLEAR_TOKEN') {
        console.log('Clearing malformed JWT token');
        localStorage.removeItem('authToken');
        localStorage.removeItem('token'); // Also clear 'token' key if it exists
        // Force a page reload to clear any cached state
        window.location.reload();
      } else {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (passwords) => api.post('/auth/change-password', passwords),
};

// Organization API
export const organizationAPI = {
  // Companies
  getCompanies: () => api.get('/organization/companies'),
  getCompanyById: (id) => api.get(`/organization/companies/${id}`),
  createCompany: (data) => api.post('/organization/companies', data),
  updateCompany: (id, data) => api.put(`/organization/companies/${id}`, data),
  
  // Divisions
  getDivisions: (params = {}) => api.get('/organization/divisions', { params }),
  createDivision: (data) => api.post('/organization/divisions', data),
  updateDivision: (id, data) => api.put(`/organization/divisions/${id}`, data),
  
  // Organizational Positions
  getPositions: (params = {}) => api.get('/organization/positions', { params }),
  createPosition: (data) => api.post('/organization/positions', data),
  updatePosition: (id, data) => api.put(`/organization/positions/${id}`, data),
  
  // Hierarchy Views
  getOrganizationalHierarchy: (params = {}) => api.get('/organization/hierarchy', { params }),
  getEmployeeReportingStructure: (params = {}) => api.get('/organization/reporting-structure', { params }),
  
  // Company Settings
  getCompanySettings: (companyId, category = '') => api.get(`/organization/companies/${companyId}/settings`, { 
    params: category ? { category } : {}
  }),
  updateCompanySettings: (companyId, settings) => api.put(`/organization/companies/${companyId}/settings`, { settings }),
};

// Employee API
export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  deactivate: (id, reason) => api.patch(`/employees/${id}/deactivate`, { reason }),
  getDepartments: () => api.get('/employees/departments'),
  getUnassociatedUsers: (params) => api.get('/employees/unassociated-users', { params }),
  autoLinkByEmail: (id, data) => api.post(`/employees/${id}/auto-link-user`, data),
};

// Departments API
export const departmentsAPI = {
  getAll: (params) => api.get('/departments', { params }),
  getActive: () => api.get('/departments/active'),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  getStats: () => api.get('/departments/stats/overview'),
};

// Attendance API
export const attendanceAPI = {
  clockIn: (data = {}, config = {}) => api.post('/attendance/clock-in', data, config),
  clockOut: (data = {}, config = {}) => api.post('/attendance/clock-out', data, config),
  startBreak: (data = {}, config = {}) => api.post('/attendance/break-start', data, config),
  endBreak: (data = {}, config = {}) => api.post('/attendance/break-end', data, config),
  getToday: (config = {}) => api.get('/attendance/today', config),
  getHistory: (params, config = {}) => api.get('/attendance/history', { params, ...config }),
  getTeamAttendance: (params) => api.get('/attendance/team', { params }),
  updateAttendance: (id, data) => api.put(`/attendance/${id}`, data),
  updateAttendanceEvent: (eventId, data) => api.put(`/attendance/event/${eventId}`, data),
  updateAttendanceTime: (data) => api.put('/attendance/update-time', data),
  getTeamAttendanceRange: (params) => api.get('/attendance/team/range', { params }),
  createAttendanceRecord: (data) => api.post('/attendance/manual-record', data),
  getUserPermissions: () => api.get('/attendance/permissions'),
  getRolePermissions: () => api.get('/attendance/settings/roles'),
  updateRolePermission: (data) => api.post('/attendance/settings/roles', data),
  updateAttendanceSettings: (data) => api.post('/attendance/settings', data),
  getEmployees: () => api.get('/employees'),
};

// Leave API
export const leaveAPI = {
  // Leave requests
  getRequests: (params) => api.get('/leave', { params }),
  getById: (id) => api.get(`/leave/${id}`),
  create: (data) => api.post('/leave', data),
  approve: (id, data) => api.post(`/leave/${id}/approve`, data),
  reject: (id, data) => api.post(`/leave/${id}/reject`, data),
  cancel: (id, data) => api.post(`/leave/${id}/cancel`, data),
  
  // Leave balance and types
  getBalance: (params) => api.get('/leave/balance', { params }),
  getTypes: () => api.get('/leave/types'),
  
  // Leave settings (admin/HR only)
  getSettings: () => api.get('/leave/settings/config'),
  updateSettings: (data) => api.put('/leave/settings/config', data),
  
  // Legacy support
  getUpcoming: () => api.get('/leave', { params: { status: 'approved' } }),
  getHistory: (params) => api.get('/leave', { params }),
  requestLeave: (data) => api.post('/leave', data),
  updateRequest: (id, data) => api.put(`/leave/${id}`, data),
  deleteRequest: (id) => api.delete(`/leave/${id}`),
  getTeamRequests: (params) => api.get('/leave', { params: { ...params, manager_view: 'true' } }),
  approveRequest: (id) => api.post(`/leave/${id}/approve`, {}),
  rejectRequest: (id, reason) => api.post(`/leave/${id}/reject`, { rejection_reason: reason }),
};

// Payroll API
export const payrollAPI = {
  getAll: (params) => api.get('/payroll/records', { params }),
  getById: (id) => api.get(`/payroll/records/${id}`),
  getSummary: (params) => api.get('/payroll/summary', { params }),
  generate: (data) => api.post('/payroll/generate', data),
  getPayslip: (id) => api.get(`/payroll/payslip/${id}`, { responseType: 'blob' }),
  update: (id, data) => api.put(`/payroll/records/${id}`, data),
  delete: (id) => api.delete(`/payroll/records/${id}`),
  getEmployees: () => api.get('/payroll/employees'),
  getPayPeriods: () => api.get('/payroll/pay-periods'),
};

// Reports API
export const reportsAPI = {
  getDashboard: (params) => api.get('/reports/dashboard', { params }),
  getAttendance: (params) => api.get('/reports/attendance', { params }),
  getLeave: (params) => api.get('/reports/leave', { params }),
  getPayroll: (params) => api.get('/reports/payroll', { params }),
  getEmployee: (params) => api.get('/reports/employee', { params }),
  getEarlyCheckin: (params) => api.get('/reports/early-checkin', { params }),
  export: (type, format, params) => api.get(`/reports/export/${type}/${format}`, { params }),
};

// UK Scheduling API (New comprehensive scheduling system)
export const ukSchedulingAPI = {
  // Rotas
  getRotas: (params) => api.get('/scheduling/rotas', { params }),
  createRota: (data) => api.post('/scheduling/rotas', data),
  publishRota: (id) => api.post(`/scheduling/rotas/${id}/publish`),
  updateRota: (id, data) => api.put(`/scheduling/rotas/${id}`, data),
  
  // Shifts
  getShifts: (params) => api.get('/scheduling/shifts', { params }),
  getMyShifts: (params) => api.get('/scheduling/shifts/my-shifts', { params }),
  getAvailableShifts: (params) => api.get('/scheduling/shifts/available', { params }),
  createShift: (data) => api.post('/scheduling/shifts', data),
  updateShift: (shiftId, data) => api.put(`/scheduling/shifts/${shiftId}`, data),
  createShiftFromTemplate: (data) => api.post('/scheduling/shifts/from-template', data),
  assignShift: (shiftId, data) => api.post(`/scheduling/shifts/${shiftId}/assign`, data),
  unassignShift: (shiftId) => api.delete(`/scheduling/shifts/${shiftId}/unassign`),
  
  // Shift Templates
  getTemplates: (params) => api.get('/scheduling/templates', { params }),
  createTemplate: (data) => api.post('/scheduling/templates', data),
  
  // Scheduling Intelligence
  getSuggestions: (rotaId, params) => api.get(`/scheduling/suggestions/${rotaId}`, { params }),
  getEmployeeAvailability: (employeeId, params) => api.get(`/scheduling/employee/${employeeId}/availability`, { params }),
  
  // Shift Swaps
  getShiftSwaps: (params) => api.get('/scheduling/shift-swaps', { params }),
  createShiftSwap: (data) => api.post('/scheduling/shift-swaps', data),
  createShiftSwapRequest: (data) => api.post('/scheduling/shift-swaps', data),
  respondToShiftSwap: (swapId, data) => api.post(`/scheduling/shift-swaps/${swapId}/respond`, data),
  cancelShiftSwap: (swapId) => api.delete(`/scheduling/shift-swaps/${swapId}`),
  
  // Compliance
  validateAction: (data) => api.post('/scheduling/validate', data),
  getDashboard: (params) => api.get('/scheduling/dashboard', { params }),
};

// Enhanced Time & Attendance API
export const timeAttendanceAPI = {
  // Clock operations (enhanced)
  clockIn: (data, config = {}) => api.post('/time-attendance/clock-in', data, config),
  clockOut: (data, config = {}) => api.post('/time-attendance/clock-out', data, config),
  startBreak: (data, config = {}) => api.post('/time-attendance/break/start', data, config),
  endBreak: (data, config = {}) => api.post('/time-attendance/break/end', data, config),
  getStatus: (employeeId) => api.get(`/time-attendance/status/${employeeId}`),
  
  // Timesheet Management
  getTimesheets: (params) => api.get('/time-attendance/timesheets', { params }),
  getPendingTimesheets: (params) => api.get('/time-attendance/timesheets/pending', { params }),
  approveTimesheet: (timesheetId, data) => api.post(`/time-attendance/timesheets/${timesheetId}/approve`, data),
  rejectTimesheet: (timesheetId, data) => api.post(`/time-attendance/timesheets/${timesheetId}/reject`, data),
  
  // Reporting & Compliance
  getDashboard: (params) => api.get('/time-attendance/dashboard', { params }),
  getWTRCompliance: (employeeId, params) => api.get(`/time-attendance/compliance/${employeeId}`, { params }),
  getAttendanceSummary: (params) => api.get('/time-attendance/reports/attendance-summary', { params }),
  
  // Kiosk Mode
  kioskClockAction: (data) => api.post('/time-attendance/kiosk/clock-action', data),
  getKioskStatus: (employeeNumber) => api.get(`/time-attendance/kiosk/status/${employeeNumber}`),
  
  // Admin Operations
  autoClockOut: () => api.post('/time-attendance/auto-clockout'),
};

// Document Management API (Enhanced)
export const documentAPI = {
  // Employee Documents
  getEmployeeDocuments: (employeeId, params) => api.get(`/documents/employee/${employeeId}`, { params }),
  uploadEmployeeDocument: (employeeId, formData) => api.post(`/documents/employee/${employeeId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadDocument: (documentId) => api.get(`/documents/download/${documentId}`, { responseType: 'blob' }),
  deleteDocument: (documentId) => api.delete(`/documents/${documentId}`),
  
  // Document Categories and Templates
  getCategories: () => api.get('/documents/categories'),
  getTemplates: () => api.get('/documents/templates'),
  
  // Document Notifications
  getNotificationSettings: () => api.get('/document-settings/notifications'),
  updateNotificationSettings: (data) => api.put('/document-settings/notifications', data),
};

// Probationary Management API (New)
export const probationaryAPI = {
  // Settings
  getSettings: () => api.get('/probationary/settings'),
  updateSettings: (data) => api.put('/probationary/settings', data),
  
  // Employee Probation
  getEmployeeProbation: (employeeId) => api.get(`/probationary/employee/${employeeId}`),
  getAllProbationaryEmployees: (params) => api.get('/probationary/employees', { params }),
  updateProbationStatus: (employeeId, data) => api.put(`/probationary/employee/${employeeId}`, data),
  
  // Reviews
  getReviews: (employeeId, params) => api.get(`/probationary/reviews/${employeeId}`, { params }),
  createReview: (data) => api.post('/probationary/reviews', data),
  updateReview: (reviewId, data) => api.put(`/probationary/reviews/${reviewId}`, data),
  submitReview: (reviewId) => api.post(`/probationary/reviews/${reviewId}/submit`),
  approveReview: (reviewId, data) => api.post(`/probationary/reviews/${reviewId}/approve`, data),
  
  // Document Uploads for Reviews
  uploadReviewDocument: (reviewId, formData) => api.post(`/probationary/reviews/${reviewId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Dashboard
  getDashboard: (params) => api.get('/probationary/dashboard', { params }),
  getUpcomingReviews: (params) => api.get('/probationary/upcoming', { params }),
};

// Schedules API (Traditional Employee Scheduling System)
export const schedulesAPI = {
  // Schedule Templates
  getTemplates: () => api.get('/schedules/templates'),
  createTemplate: (data) => api.post('/schedules/templates', data),
  updateTemplate: (id, data) => api.put(`/schedules/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/schedules/templates/${id}`),
  
  // Weekly Patterns
  getWeeklyPatterns: () => api.get('/schedules/weekly-patterns'),
  createWeeklyPattern: (data) => api.post('/schedules/weekly-patterns', data),
  updateWeeklyPattern: (id, data) => api.put(`/schedules/weekly-patterns/${id}`, data),
  deleteWeeklyPattern: (id) => api.delete(`/schedules/weekly-patterns/${id}`),
  applyWeeklyPattern: (data) => api.post('/schedules/apply-pattern', data),
  
  // Employee Schedules
  getSchedules: (params) => api.get('/schedules', { params }),
  createOrUpdateSchedule: (data) => api.post('/schedules', data),
  getScheduleForDate: (employeeId, date) => api.get(`/schedules/employee/${employeeId}/date/${date}`),
  getSchedulesByEmployee: (employeeId, params) => api.get(`/schedules/employee/${employeeId}`, { params }),
  deleteSchedule: (scheduleId, data) => api.delete(`/schedules/${scheduleId}`, { data }),
  
  // History and Audit
  getScheduleHistory: (params) => api.get('/schedules/history', { params }),
  
  // Unscheduled Working Days
  getUnscheduledDays: (params) => api.get('/schedules/unscheduled-days', { params }),

  // Bulk Operations
  bulkCreateSchedules: (data) => api.post('/schedules/bulk-create', data),
  bulkDeleteSchedules: (data) => api.post('/schedules/bulk-delete', data),
  copySchedules: (data) => api.post('/schedules/copy-schedules', data),
};

// Legacy Time Attendance API (additional endpoints for backward compatibility)
export const legacyTimeAttendanceAPI = {
  exportTimesheets: (params) => api.get('/time-attendance/timesheets/export', { params }),
  
  // Overtime
  getOvertimeRecords: (params) => api.get('/time-attendance/overtime', { params }),
  requestOvertimeApproval: (data) => api.post('/time-attendance/overtime/request', data),
  approveOvertime: (id, data) => api.post(`/time-attendance/overtime/${id}/approve`, data),
  
  // Reports and Analytics
  getAttendanceReport: (params) => api.get('/time-attendance/reports/attendance', { params }),
  getWorkingHoursReport: (params) => api.get('/time-attendance/reports/working-hours', { params }),
  getComplianceReport: (params) => api.get('/time-attendance/reports/compliance', { params }),
};

// HMRC API
export const hmrcAPI = {
  getCompliance: () => api.get('/hmrc/compliance'),
  getPayrollData: () => api.get('/hmrc/payroll'),
  getTaxData: () => api.get('/hmrc/tax'),
  getNIData: () => api.get('/hmrc/ni'),
  generateReport: (data) => api.post('/hmrc/reports', data),
};

// User Management API
export const userManagementAPI = {
  getAll: (params) => api.get('/user-management', { params }),
  getById: (id) => api.get(`/user-management/${id}`),
  create: (data) => api.post('/user-management', data),
  update: (id, data) => api.put(`/user-management/${id}`, data),
  deactivate: (id) => api.delete(`/user-management/${id}`),
  resetPassword: (id) => api.post(`/user-management/${id}/reset-password`),
  resendInvitation: (id) => api.post(`/user-management/${id}/resend-invitation`),
};

// Role Management API
export const roleManagementAPI = {
  getAllRoles: () => api.get('/role-management/roles'),
  getAllPermissions: () => api.get('/role-management/permissions'),
  getRolePermissions: (role) => api.get(`/role-management/roles/${role}/permissions`),
  updateRolePermissions: (role, permissions) => api.put(`/role-management/roles/${role}/permissions`, { permissions }),
  createRole: (data) => api.post('/role-management/roles', data),
  updateRole: (roleId, data) => api.put(`/role-management/roles/${roleId}`, data),
  getRoleDetails: (identifier) => api.get(`/role-management/roles/${identifier}`),
  deleteRole: (role) => api.delete(`/role-management/roles/${role}`),
  getNavigationForRole: (role) => api.get(`/role-management/roles/${role}/navigation`),
  syncUserRoleConstraints: () => api.post('/role-management/sync-constraints'),
};

// Kiosk API
export const kioskAPI = {
  getEmployees: () => api.get('/kiosk/employees'),
  authenticatePin: (employeeId, pin) => api.post('/kiosk/authenticate', { employeeId, pin }),
  getAttendanceStatus: (employeeId) => api.get(`/kiosk/attendance/${employeeId}`),
  checkIn: (employeeId, pin) => api.post('/kiosk/checkin', { employeeId, pin }),
  checkOut: (employeeId, pin) => api.post('/kiosk/checkout', { employeeId, pin }),
};

// Employee PIN API
export const employeePinAPI = {
  setPinStatus: (employeeId) => api.get(`/employees/${employeeId}/kiosk-pin/status`),
  setPin: (employeeId, pin) => api.post(`/employees/${employeeId}/kiosk-pin`, { pin }),
  removePin: (employeeId) => api.delete(`/employees/${employeeId}/kiosk-pin`),
};

// Kiosk Settings API
export const kioskSettingsAPI = {
  getSettings: () => api.get('/kiosk-settings/settings'),
  updateSettings: (settings) => api.put('/kiosk-settings/settings', settings),
  authenticateKiosk: (username, password) => api.post('/kiosk-settings/auth', { username, password }),
};

// Check-in Settings API
export const checkinSettingsAPI = {
  getSettings: () => api.get('/checkin-settings'),
  updateSettings: (settings) => api.put('/checkin-settings', settings),
};

// Holiday Settings API
export const holidaySettingsAPI = {
  getSettings: () => api.get('/holiday-settings/settings'),
  updateSettings: (settings) => api.put('/holiday-settings/settings', settings),
  getLeaveTypes: () => api.get('/holiday-settings/leave-types'),
  saveLeaveType: (data) => api.post('/holiday-settings/leave-types', data),
  updateLeaveType: (id, data) => api.put(`/holiday-settings/leave-types/${id}`, data),
  deleteLeaveType: (id) => api.delete(`/holiday-settings/leave-types/${id}`),
  allocateHolidaysForAll: (data) => api.post('/holiday-settings/allocate-all', data),
  getAllocationsSummary: (params) => api.get('/holiday-settings/allocations-summary', { params }),
};

// Documents API
export const documentsAPI = {
  getDocumentTypes: () => api.get('/documents/types'),
  getEmployeeDocuments: (employeeId) => api.get(`/documents/employee/${employeeId}`),
  uploadDocument: (formData) => {
    return api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateDocument: (documentId, data) => api.put(`/documents/${documentId}`, data),
  deleteDocument: (documentId) => api.delete(`/documents/${documentId}`),
  downloadDocument: (documentId) => api.get(`/documents/${documentId}/download`, {
    responseType: 'blob',
  }),
  getExpiringDocuments: (days) => api.get('/documents/expiring', { params: { days } }),
  getDocumentNotifications: () => api.get('/documents/notifications'),
  acknowledgeNotification: (notificationId) => api.put(`/documents/notifications/${notificationId}/acknowledge`),
  getDocumentAccessLog: (documentId) => api.get(`/documents/${documentId}/access-log`),
  getDocumentTypeSettings: () => api.get('/documents/settings/types'),
  updateDocumentTypeSettings: (settings) => api.put('/documents/settings/types', settings),
};

// Legacy Probationary API (merged with enhanced version above)
export const legacyProbationaryAPI = {
  getSettings: () => api.get('/probationary/settings'),
  updateSettings: (settings) => api.put('/probationary/settings', settings),
  createProbationaryPeriod: (data) => api.post('/probationary/periods', data),
  getProbationaryPeriod: (employeeId) => api.get(`/probationary/periods/${employeeId}`),
  getProbationaryReviews: (employeeId) => api.get(`/probationary/reviews/${employeeId}`),
  getActiveProbationaryEmployees: () => api.get('/probationary/periods/active'),
  getUpcomingReviews: (daysAhead) => api.get('/probationary/reviews/upcoming', { params: { daysAhead } }),
  completeReview: (reviewId, data) => api.put(`/probationary/reviews/${reviewId}/complete`, data),
  extendProbationaryPeriod: (employeeId, data) => api.put(`/probationary/periods/${employeeId}/extend`, data),
  uploadDocument: (formData) => api.post('/probationary/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getNotifications: (params) => api.get('/probationary/notifications', { params }),
  markNotificationRead: (notificationId) => api.put(`/probationary/notifications/${notificationId}/read`),
  getDashboardData: () => api.get('/probationary/dashboard'),
};

// Workflow Management API (New)
export const workflowAPI = {
  // Categories and templates
  getCategories: () => api.get('/workflow/categories'),
  getTemplates: () => api.get('/workflow/templates'),
  createTemplate: (data) => api.post('/workflow/templates', data),
  updateTemplate: (templateId, data) => api.put(`/workflow/templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/workflow/templates/${templateId}`),
  
  // Approvers and roles
  getApprovers: () => api.get('/workflow/approvers'),
  getRoles: () => api.get('/workflow/roles'),
  
  // Master data
  getDropdownValues: () => api.get('/workflow/dropdown-values'),
  getModules: () => api.get('/workflow/modules'),
  getModuleEntities: (moduleKey) => api.get(`/workflow/modules/${moduleKey}/entities`),
  getModuleConfig: () => api.get('/workflow/module-config'),
  
  // Organizational roles
  getOrganizationalRoles: () => api.get('/workflow/organizational-roles'),
  getEmployeesWithRoles: () => api.get('/workflow/employees-with-roles'),
  testApproverResolution: (data) => api.post('/workflow/test-approver-resolution', data),
  
  // Reviews
  submitReview: (data) => api.post('/workflow/reviews/submit', data),
  getReviewSubmissions: () => api.get('/workflow/reviews/submissions'),
  getReviewSubmissionDetails: (submissionId) => api.get(`/workflow/reviews/submissions/${submissionId}`),
  addReviewComment: (submissionId, data) => api.post(`/workflow/reviews/submissions/${submissionId}/comments`, data),
  
  // Approvals
  getPendingApprovals: () => api.get('/workflow/approvals/pending'),
  processApproval: (submissionId, data) => api.post(`/workflow/approvals/${submissionId}/process`, data),
  
  // Notifications
  getNotifications: () => api.get('/workflow/notifications'),
  markNotificationRead: (notificationId) => api.put(`/workflow/notifications/${notificationId}/read`)
};

// Centralized Role Management API (New)
export const centralizedRoleAPI = {
  getAllRoles: () => api.get('/centralized-roles'),
  getRoleDropdownOptions: () => api.get('/centralized-roles/dropdown'),
  getRoleHierarchy: () => api.get('/centralized-roles/hierarchy'),
  getEmployeesWithRoles: () => api.get('/centralized-roles/employees-with-roles'),
  getRoleAnalysis: () => api.get('/centralized-roles/analysis'),
  synchronizeRoles: () => api.post('/centralized-roles/synchronize'),
  getUserRoleInfo: (userId) => api.get(`/centralized-roles/user/${userId}`),
  updateUserRole: (userId, data) => api.put(`/centralized-roles/user/${userId}`, data),
  bulkMigrateRoles: (data) => api.post('/centralized-roles/bulk-migrate', data)
};

// Organizational Role Management API (New) 
export const organizationalRoleAPI = {
  getRoleDefinitions: () => api.get('/organizational-roles/definitions'),
  createRoleDefinition: (data) => api.post('/organizational-roles/definitions', data),
  updateRoleDefinition: (roleId, data) => api.put(`/organizational-roles/definitions/${roleId}`, data),
  deleteRoleDefinition: (roleId) => api.delete(`/organizational-roles/definitions/${roleId}`),
  getEmployeesWithRoles: () => api.get('/organizational-roles/employees'),
  assignRoleToEmployee: (data) => api.post('/organizational-roles/assign', data),
  bulkAssignRoles: (data) => api.post('/organizational-roles/bulk-assign', data),
  removeRoleAssignment: (assignmentId) => api.delete(`/organizational-roles/assignments/${assignmentId}`),
  removeRoleAssignmentByEmployeeAndRole: (employeeId, roleId) => api.delete(`/organizational-roles/assignments/employee/${employeeId}/role/${roleId}`),
  syncEmployeeRoles: () => api.post('/organizational-roles/sync'),
  getRoleStatistics: () => api.get('/organizational-roles/statistics')
};

export default api;
